import React, {useCallback, useEffect, useState, useMemo, useRef} from 'react';
import {
  Captions,
} from "../../../models/Captions";
import styles from './index.module.css';
import {useLocalStorage, useKeyPress} from 'react-use';
import {useDialogs} from "../../../ui/dialogs/DialogManager";
import {getInitialValuesFromResult} from "./AddEditWordDialog";
import {isQueryByWordId} from "languagetool-player/src/annotations";
import {
  analyzeListOfWords,
  AnalyzeResult,
  autoAssignWordsStream,
  AutoAssignDecision,
  AutoAssignDecisionCreateDraft,
  AutoAssignDecisionCustomDraft,
  AutoAssignDecisionEditDraft,
  getAPI,
  isWordNodeResult,
  WordNodeResult
} from "../wordsApi";
import {LineTable} from "./LineTable";
import {isEqual, keyBy} from 'lodash';
import {LinePreview, useLineByLineBrowse} from "../LineByLineView";
import {LineBrowser} from "../LineByLineView/LineBrowser";
import {pickWordDialogDefaults, useShowWordDialog} from "./useShowWordDialog";
import {usePlayLine} from "../../Market/utils/useAudioPlayer";
import {MediaTrack} from "../../../models/MediaTrack";
import {Button, ButtonGroup} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import {NewUnitState, Translation, UnitDataSetter} from "../../../models/Unit";
import {useIndexedContentIssues} from "../../Market/ContentIssues";
import {IssueManagerButton} from "./IssueManager";
import {WordNode} from "languagetool-player/src/models/Node";
import {Vocable, VocableId} from "languagetool-player/src/models/formats/CaptionTrack";
import {WordLikeNode} from "../../../models/Captions/wordNodes";
import {useGetTrackAPIForCaptions, useGetTrackInterfaceForSimplePictureBook} from "../../Market/useGetTrackInterface";
import {SimplePictureBook} from "languagetool-player/src/models/formats/SimplePictureBook";
import {generatePlaySegmentsForPage} from "languagetool-player/src/ui/SimplePictureBook/useBookPlayer";
import {URLResolver} from "languagetool-player/src/ui/SimplePictureBook/SimplePictureBookContext";
import {useAudioPlayer} from "languagetool-player/src/ui/Audio/useAudioPlayer";
import {CustomWordDialogResult, CustomWordNodeDialog} from "./CustomWordNodeDialog";


export type NodeSuggestions = {[key: string]: AnalyzeResult[]};


export type WordDef = {id: number, meaningId?: number};

type AIDraftDecision = AutoAssignDecisionCreateDraft|AutoAssignDecisionEditDraft|AutoAssignDecisionCustomDraft;
export type AIDecisionNote = {
  action: AutoAssignDecision['action'],
  reason?: string,
  confidence?: string,
};

function getLineDisplayText(vocables: {text: string}[]): string {
  return vocables.map(v => v.text).join(" ");
}

function getLineContextText(vocables: {text: string, pre?: string, post?: string}[]): string {
  return vocables.map(v => `${v.pre || ''}${v.text}${v.post || ''}`).join('').trim();
}

function sortVocableIdsByLineOrder(vocableIds: Iterable<string>, vocables: {id: string}[]): string[] {
  const wanted = new Set(Array.from(vocableIds));
  return vocables.map(v => v.id).filter(vId => wanted.has(vId));
}

function getCurrentNodeSpansForLine(trackIntf: TrackInterface, vocables: Vocable[]): string[][] {
  const spans: string[][] = [];
  const seen = new Set<string>();

  for (const vocable of vocables) {
    const node = trackIntf.getWordNodeForVocables([vocable.id], {allowPartialMatch: true});
    if (!node) {
      continue;
    }
    const key = node.vocables.join('|');
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    spans.push(node.vocables);
  }

  return spans;
}

function getMergeKey(vocableIds: string[]): string {
  return vocableIds.join('|');
}

function doesMergeTouchVocableIds(merge: Set<string>, vocableIds: string[]) {
  return vocableIds.some(vId => merge.has(vId));
}

/**
 * Index node suggestions by vocable id.
 */
function indexNodeSuggestions(nodeSuggestions: AnalyzeResult[]|null): NodeSuggestions {
  const db: NodeSuggestions = {};
  if (nodeSuggestions) {
    nodeSuggestions.forEach(suggestion => {
      suggestion.node.vocables.forEach(vocableId => {
        if (!db[vocableId]) {
          db[vocableId] = [];
        }
        db[vocableId].push(suggestion);
      })
    })
  };

  return db;
}


export type TrackInterface = {
  setVocableText: (vocableId: string, text: string) => void
  setVocableAlternates: (vId: string, alternates: { [key: string]: string|undefined }) => void,
  getWordNodeForVocables: (vid: string[], opts?: {allowPartialMatch?: boolean}) => WordLikeNode|null,
  setWordNode: (node: WordLikeNode) => void,
  removeWordNode: (vocableId: string) => void,
  getLineCount: () => number
  getLine: (idx: number) => Vocable[]
}

export type PlayInterface = {
  playLine(idx: number): void;
}


type Props = {
  // This object must update itself on every change.
  trackIntf: TrackInterface,
  playIntf?: PlayInterface,

  language: string,
  trackVersionId?: string,

  apiUrl: string,

  // Some of those are specific to the caption still
  unitId?: string|null,
  mediaTrack?: MediaTrack,
  translation?: Translation,
  initialLineIndex?: number,
  captions?: Captions,
  extraButtons?: any
};


export function WordAssignScreenCaptionsTrack(props: Omit<Props, 'trackIntf'> & {captions: Captions}) {
  const trackIntf = useGetTrackAPIForCaptions(props.captions);

  const playLine = usePlayLine({
    mediaTrack: props.mediaTrack,
    track: props.captions,
  });
  const playIntf = useMemo(() => {
    return {playLine};
  }, [playLine]);

  return <WordAssignScreen
    {...props}
    captions={props.captions}
    trackIntf={trackIntf}
    playIntf={playIntf}
  />
}


export function WordAssignScreenSimplePictureBook(props: Omit<Props, 'trackIntf'> & {
  book: SimplePictureBook,
  setData: UnitDataSetter<SimplePictureBook>,
  unitState: NewUnitState<SimplePictureBook>
}) {
  const trackIntf = useGetTrackInterfaceForSimplePictureBook(props.book, props.setData, props.unitState.idGen);

  const player = useAudioPlayer();
  const fileResolver = makeResolver(props.unitState.files);
  const playIntf = useMemo(() => {
    return {
      playLine: (lineIdx: number) => {
        const segments = generatePlaySegmentsForPage({
          // TODO: Only works because page = one paragraph = one blob for now
          page: props.book.pages[lineIdx],
          media: props.book.media
        });
        let url = segments[0].url;
        const result = fileResolver(url);
        player.play(result.uri);
      }
    };
  }, [player, fileResolver]);

  return <WordAssignScreen
    {...props}
    captions={props.captions}
    trackIntf={trackIntf}
    playIntf={playIntf}
  />
}

function makeResolver(files: {name: string; url: string}[]|undefined): URLResolver {
  const filesMap = keyBy(files, "name");
  return (url, opts) => {
    if (url.startsWith("container://")) {
      const filename = url.slice(12);
      const resolvedUrl = filesMap[filename]?.url;
      return {uri: resolvedUrl};
    }

    return {uri: url};
  };
}


export function WordAssignScreen(props: Props) {
  const {trackIntf, trackVersionId} = props;
  const [contentIssues, _] = useIndexedContentIssues(props.unitId, props.captions);

  const [, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [hasData, setHasData] = useState(false);
  const [nodeSuggestions, setNodeSuggestions] = useState<NodeSuggestions>({});
  const [forcedVocableMerges, setForcedVocableMerges] = useState<Set<string>[]>([]);
  const [aiForcedVocableMerges, setAIForcedVocableMerges] = useState<Set<string>[]>([]);
  const [aiDrafts, setAIDrafts] = useState<{[key: string]: AIDraftDecision}>({});
  const [aiDecisionNotes, setAIDecisionNotes] = useState<{[key: string]: AIDecisionNote}>({});
  const [autoAssignState, setAutoAssignState] = useState({
    isRunning: false,
    totalLines: 0,
    totalTargets: 0,
    completedLines: 0,
    resolved: 0,
    skipped: 0,
    errors: 0,
  });
  const [autoAssignController, setAutoAssignController] = useState<AbortController|null>(null);

  const {words, currentLineIndex, setCurrentLineIdx} = useLineByLineBrowse({
    trackAPI: props.trackIntf,
    initialLine: props.initialLineIndex
  });

  const combinedForcedVocableMerges = useMemo(() => {
    return [...forcedVocableMerges, ...aiForcedVocableMerges];
  }, [forcedVocableMerges, aiForcedVocableMerges]);

  const currentLineForcedVocableMerges = useMemo(() => {
    const currentLineVocableIds = new Set(words.map(v => v.id));
    return combinedForcedVocableMerges
      .filter(merge => Array.from(merge).every(vId => currentLineVocableIds.has(vId)))
      .map(merge => new Set(sortVocableIdsByLineOrder(merge, words)));
  }, [combinedForcedVocableMerges, words]);

  const currentLineForcedMergeKey = useMemo(() => {
    return currentLineForcedVocableMerges
      .map(merge => getMergeKey(Array.from(merge)))
      .join(',');
  }, [currentLineForcedVocableMerges]);

  // Make sure this callback is not replaced every time we assign a structure node, only
  // if the line changes. IMPORTANT! If this is not properly memoized we will loop.
  const reloadSuggestions = useCallback(() => {
    setIsLoading(true);

    // Send all existing multi-vocable nodes on the current line to the server so we get up-to-date
    // suggestions for them.
    const compoundVocables: Set<string>[] = Array.from(currentLineForcedVocableMerges);

    words.map((vocable, idx) => {
      const wordNode = trackIntf.getWordNodeForVocables([vocable.id], {allowPartialMatch: true});
      if (!wordNode) {
        return;
      }
      if (wordNode.vocables.length <=1 ) {
        return;
      }
      compoundVocables.push(new Set(wordNode.vocables));
    })

    return analyzeListOfWords(words, props.language, compoundVocables, {apiUrl: props.apiUrl})
      .then(r => {
        const indexed = indexNodeSuggestions(r);
        setNodeSuggestions(indexed);
        setHasData(true);
        setError(null);
        return indexed;
      })
      .catch((e) => {
        setIsLoading(false);
        setError(e);
      });
  }, [currentLineIndex, trackIntf, words, setIsLoading, setHasData, setNodeSuggestions, currentLineForcedVocableMerges]);

  const reloadSuggestionsRef = useRef(reloadSuggestions);
  useEffect(() => {
    reloadSuggestionsRef.current = reloadSuggestions;
  }, [reloadSuggestions]);

  // Re-analyze:
  // - When the current line changes.
  // - When the current line text changes.
  // - When current-line forced merges change.
  // But do not reanalyze while auto-assign is running; otherwise unrelated AI drafts on other
  // lines can trigger a flood of /analyze_words requests for the current line.
  const texts = words.map(v => v.text);
  useEffect(() => {
    if (autoAssignState.isRunning) {
      return;
    }
    setNodeSuggestions({});
    setHasData(false);
    reloadSuggestionsRef.current();
  }, [setNodeSuggestions, setHasData, currentLineIndex, JSON.stringify(texts), currentLineForcedMergeKey, autoAssignState.isRunning]);

  const dialogs = useDialogs();

  const handleCustomNodeEdited = useCallback((vocableIds: VocableId[], result: WordNode) => {
    trackIntf.setWordNode(result);
  }, [trackIntf]);

  const showWordDialog = useShowWordDialog({
    language: props.language,
    onSave: async (word?: WordDef) => {
      await reloadSuggestions();
      // If there is just one result with this new word id now, auto-select it?
      // Note that we cannot just add the node suggestion which the user clicked,
      // and patch in the new word id. For example, the word the user entered
      // might not actually resolve to the grammar form.
    },
  });

  const clearAIDraftForVocables = useCallback((vocableIds: string[]) => {
    const key = getMergeKey(vocableIds);
    setAIDrafts(prev => {
      if (!(key in prev)) {
        return prev;
      }
      const next = {...prev};
      delete next[key];
      return next;
    });
  }, [setAIDrafts]);

  const removeAIDraftsForVocables = useCallback((vocableIds: string[], opts?: {exactOnly?: boolean}) => {
    const key = getMergeKey(vocableIds);
    const shouldRemoveKey = (candidateKey: string) => {
      const candidateVocables = candidateKey.split('|');
      return opts?.exactOnly
        ? candidateKey === key
        : candidateVocables.some(vId => vocableIds.indexOf(vId) > -1);
    };

    setAIDrafts(prev => {
      const next = {...prev};
      Object.keys(next).forEach(draftKey => {
        if (shouldRemoveKey(draftKey)) {
          delete next[draftKey];
        }
      });
      return next;
    });

    setAIDecisionNotes(prev => {
      const next = {...prev};
      Object.keys(next).forEach(noteKey => {
        if (shouldRemoveKey(noteKey)) {
          delete next[noteKey];
        }
      });
      return next;
    });

    setAIForcedVocableMerges(prev => prev.filter(merge => {
      if (opts?.exactOnly) {
        return getMergeKey(Array.from(merge)) !== key;
      }
      return !doesMergeTouchVocableIds(merge, vocableIds);
    }));
  }, [setAIDrafts, setAIDecisionNotes, setAIForcedVocableMerges]);

  const storeAIDraft = useCallback((draft: AIDraftDecision) => {
    const key = getMergeKey(draft.vocableIds);
    setAIDrafts(prev => ({
      ...prev,
      [key]: draft,
    }));

    if (draft.vocableIds.length > 1) {
      setAIForcedVocableMerges(prev => {
        if (prev.find(merge => isEqual(new Set(merge), new Set(draft.vocableIds)))) {
          return prev;
        }
        return [...prev, new Set(draft.vocableIds)];
      });
    }
  }, [setAIDrafts, setAIForcedVocableMerges]);

  const storeAIDecisionNote = useCallback((decision: AutoAssignDecision) => {
    const key = getMergeKey(decision.vocableIds);
    setAIDecisionNotes(prev => ({
      ...prev,
      [key]: {
        action: decision.action,
        reason: decision.reason,
        confidence: decision.confidence,
      }
    }));
  }, [setAIDecisionNotes]);

  const showCustomNodeDialog = useCallback((opts: {
    vocableIds: VocableId[],
    subtitleText: string,
    initialValues?: {
      wordId?: number,
      customDiacritization?: string,
    },
    onOk?: () => void,
  }) => {
    dialogs.show(CustomWordNodeDialog, {
      subtitleText: opts.subtitleText,
      initialValues: opts.initialValues,
      onOk: (data: CustomWordDialogResult) => {
        handleCustomNodeEdited(opts.vocableIds, {
          id: '',
          vocables: opts.vocableIds,
          type: 'word',
          wordId: {id: data.wordId},
          isManual: {
            diacritization: data.diacritization
          }
        });
        if (opts.onOk) {
          opts.onOk();
        }
      }
    });
  }, [dialogs, handleCustomNodeEdited]);

  const handleApplyAIDraft = useCallback((draft: AIDraftDecision) => {
    if (draft.action === 'create_word_draft') {
      showWordDialog({
        initialValues: draft.initialValues,
        matchText: draft.matchText,
        onSave: async () => {
          clearAIDraftForVocables(draft.vocableIds);
          await reloadSuggestions();
        }
      });
      return;
    }

    if (draft.action === 'edit_word_draft') {
      showWordDialog({
        wordId: draft.wordId,
        initialValues: draft.initialValues,
        matchText: draft.matchText,
        onSave: async () => {
          clearAIDraftForVocables(draft.vocableIds);
          await reloadSuggestions();
        }
      });
      return;
    }

    showCustomNodeDialog({
      vocableIds: draft.vocableIds,
      subtitleText: draft.matchText,
      initialValues: {
        wordId: draft.wordId,
        customDiacritization: draft.diacritization,
      },
      onOk: () => {
        removeAIDraftsForVocables(draft.vocableIds, {exactOnly: true});
      }
    });
  }, [showWordDialog, showCustomNodeDialog, clearAIDraftForVocables, removeAIDraftsForVocables, reloadSuggestions]);

  // "Create Word" button
  const handleCreateWordClick = useCallback((text: string, vocableIds: string[]) => {
    showWordDialog(
      {
        initialValues: {text},
        ...pickWordDialogDefaults({
          vocableIds,
          nodeSuggestions,
          trackAPI: props.trackIntf
        }),
        matchText: text,
      }
    );
  }, [showWordDialog, nodeSuggestions, trackIntf]);

  // "Edit Word" button
  const handleEditWordClick = useCallback(async (wordId: number, vocableText: string) => {
    const wordData = await getAPI().getWord(wordId);

    showWordDialog({
      wordId: wordId,
      initialValues: {
        text: wordData.base,
        type: wordData.type,
        wordData: wordData.data,
      },
      matchText: vocableText,
    });
  }, [dialogs, props.language, reloadSuggestions]);

  const handleMergeVocables = useCallback((vocableIds: Set<string>) => {
    setForcedVocableMerges([
      ...forcedVocableMerges,
      vocableIds
    ])
  }, [forcedVocableMerges, setForcedVocableMerges]);

  const handleSplitVocables = useCallback((vocableIds: Set<string>) => {
    // If the vocables are in a forced merge, remove it
    const newForcedMerges = forcedVocableMerges.filter(merge => !isEqual(new Set(merge), new Set(vocableIds)));
    setForcedVocableMerges(newForcedMerges);
    setAIForcedVocableMerges(prev => prev.filter(merge => !isEqual(new Set(merge), new Set(vocableIds))));
    removeAIDraftsForVocables(Array.from(vocableIds));

    for (const vocableId of vocableIds) {
      // If there is a word node, remove it.
      trackIntf.removeWordNode(vocableId);
      trackIntf.setVocableAlternates(vocableId, {
        'sounds': undefined,
        'diacritics-auto': undefined
      });
    }
  }, [trackIntf, forcedVocableMerges, setForcedVocableMerges, setAIForcedVocableMerges, removeAIDraftsForVocables]);

  const handleWordNodeSelected = useCallback((vocableIds: VocableId[], result: AnalyzeResult|null) => {
    // A missing result indicates the user unselected all nodes, so remove whatever node is assigned.
    if (!result) {
      trackIntf.removeWordNode(vocableIds[0]);
      for (const vocableId of vocableIds) {
        trackIntf.setVocableAlternates(vocableId, {
          'sounds': undefined,
          'diacritics-auto': undefined
        });
      }
      return;
    };

    const node = result.node;

    // The word node selected refers to a base word by string, which means it was detected on the server
    // side either with rules only, or possibly with a simple word list, but not based on word which is
    // in our database. We want to migrate all words to our database, step for step, so at this point,
    // we force the user to add it now.
    if (isWordNodeResult(result) && !isQueryByWordId(result.node.wordId)) {
      showWordDialog({
        initialValues: getInitialValuesFromResult(result as WordNodeResult<any>),
      });
    }

    // The word node selected already refers to a word in our word database, so we can assign this
    // word node directly to the vocable.
    else {
      trackIntf.setWordNode(node);
      removeAIDraftsForVocables(node.vocables);

      if ('alternates' in result && result.alternates) {
        for (const vId of node.vocables) {
          const alternatesForThisVocable = result.alternates[vId];
          if (alternatesForThisVocable) {
            trackIntf.setVocableAlternates(vId, alternatesForThisVocable);
          }
        }
      }
    }
  }, [trackIntf, showWordDialog, removeAIDraftsForVocables]);

  const handleVocableTextChange = useCallback((vocableId: string, text: string) => {
    removeAIDraftsForVocables([vocableId]);
    trackIntf.setVocableText(vocableId, text);
    reloadSuggestions();
  }, [trackIntf, reloadSuggestions, removeAIDraftsForVocables]);

  const applyAutoAssignDecision = useCallback((decision: AutoAssignDecision) => {
    if (decision.action === 'assign_option') {
      handleWordNodeSelected(decision.vocableIds, decision.result);
      storeAIDecisionNote(decision);
      return;
    }

    if (decision.action === 'skip' || decision.action === 'error') {
      storeAIDecisionNote(decision);
      return;
    }

    storeAIDecisionNote(decision);
    storeAIDraft(decision);
  }, [handleWordNodeSelected, storeAIDecisionNote, storeAIDraft]);

  const applyAutoAssignDecisionRef = useRef(applyAutoAssignDecision);
  useEffect(() => {
    applyAutoAssignDecisionRef.current = applyAutoAssignDecision;
  }, [applyAutoAssignDecision]);

  const handleAutoAssignCancel = useCallback(() => {
    if (autoAssignController) {
      autoAssignController.abort();
    }
  }, [autoAssignController]);

  const handleAutoAssignStart = useCallback(async () => {
    const lines = Array.from({length: trackIntf.getLineCount()}).map((_, idx) => {
      const vocables = trackIntf.getLine(idx);
      const currentNodeSpans = getCurrentNodeSpansForLine(trackIntf, vocables);
      const assignedIds = new Set(currentNodeSpans.flatMap(x => x));
      const lineVocables = vocables.map(v => ({
        id: v.id,
        text: v.text,
        pre: v.pre,
        post: v.post,
      }));
      return {
        lineIndex: idx,
        prevText: idx > 0 ? getLineContextText(trackIntf.getLine(idx - 1)) : '',
        currentText: getLineContextText(vocables),
        nextText: idx < trackIntf.getLineCount() - 1 ? getLineContextText(trackIntf.getLine(idx + 1)) : '',
        translation: props.translation?.[idx]?.join(' ') || null,
        vocables: lineVocables,
        currentNodeSpans,
        forcedMerges: combinedForcedVocableMerges
          .filter(merge => Array.from(merge).every(vId => vocables.some(v => v.id === vId)))
          .map(merge => sortVocableIdsByLineOrder(merge, vocables)),
        hasUnassignedVocables: vocables.some(v => !assignedIds.has(v.id))
      };
    }).filter(line => line.hasUnassignedVocables).map(({hasUnassignedVocables, ...line}) => line);

    if (!lines.length) {
      setAutoAssignState({
        isRunning: false,
        totalLines: 0,
        totalTargets: 0,
        completedLines: 0,
        resolved: 0,
        skipped: 0,
        errors: 0,
      });
      return;
    }

    const controller = new AbortController();
    setAutoAssignController(controller);
    setAutoAssignState({
      isRunning: true,
      totalLines: lines.length,
      totalTargets: 0,
      completedLines: 0,
      resolved: 0,
      skipped: 0,
      errors: 0,
    });

    try {
      await autoAssignWordsStream({
        lang: props.language,
        lines,
        options: {
          fillOnlyUnassigned: true,
          maxMergeSpan: 2,
          model: 'gpt-5.4',
          reasoningEffort: 'low',
          concurrency: 3,
          maxRepairAttempts: 4,
        }
      }, {
        apiUrl: props.apiUrl,
        signal: controller.signal,
        onEvent: async (event) => {
          if (event.type === 'started') {
            setAutoAssignState(prev => ({
              ...prev,
              totalLines: event.totalLines,
              totalTargets: event.totalTargets,
            }));
            return;
          }

          if (event.type === 'decision') {
            applyAutoAssignDecisionRef.current(event.decision);
            return;
          }

          if (event.type === 'line_done') {
            setAutoAssignState(prev => ({
              ...prev,
              completedLines: event.completedLines,
              resolved: prev.resolved + event.resolved,
              skipped: prev.skipped + event.skipped,
              errors: prev.errors + (event.errors || 0),
            }));
            return;
          }

          if (event.type === 'line_error') {
            setAutoAssignState(prev => ({
              ...prev,
              completedLines: event.completedLines,
              errors: prev.errors + 1,
            }));
            return;
          }

          if (event.type === 'done') {
            setAutoAssignState(prev => ({
              ...prev,
              isRunning: false,
              completedLines: event.completedLines,
              resolved: event.resolved,
              skipped: event.skipped,
              errors: event.errors,
            }));
          }
        }
      });
    }
    catch (e: any) {
      if (e?.name !== 'AbortError') {
        console.error('Auto assign failed', e);
      }
    }
    finally {
      setAutoAssignController(null);
      setAutoAssignState(prev => ({
        ...prev,
        isRunning: false,
      }));
    }
  }, [trackIntf, props.translation, props.language, props.apiUrl, combinedForcedVocableMerges]);

  const [shiftKeyPressed,] = useKeyPress('F10');

  const lineCount = trackIntf.getLineCount();

  return <div className={styles.root}>

    <div style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between'
    }}>
      <div className={styles.topLeftControls}>
        {props.extraButtons}
        <Button
          size={'small'}
          variant={'contained'}
          onClick={handleAutoAssignStart}
          disabled={autoAssignState.isRunning}
        >
          Auto-fill Empty Words
        </Button>
        {autoAssignState.isRunning ? <Button
          size={'small'}
          onClick={handleAutoAssignCancel}
        >
          Cancel
        </Button> : null}
        {(autoAssignState.totalLines || autoAssignState.isRunning) ? <div className={styles.autoAssignStatus}>
          {autoAssignState.isRunning ? 'AI running' : 'AI done'} · {autoAssignState.completedLines}/{autoAssignState.totalLines} lines
          {autoAssignState.totalTargets ? <> · {autoAssignState.totalTargets} targets</> : null}
          {' '}· {autoAssignState.resolved} resolved
          {' '}· {autoAssignState.skipped} skipped
          {autoAssignState.errors ? <> · {autoAssignState.errors} errors</> : null}
        </div> : null}
      </div>

      {/*{(shiftKeyPressed && props.captions) ? <Button onClick={() => {*/}
      {/*  const data = slateValueToCaptions(new Captions(editor.children as CaptionTrackRoot));*/}
      {/*  //alert("Function Temporarily not available.")*/}


      {/*  // function removeVowelsFromVocables(captions: CaptionTrack): void {*/}
      {/*  for (const lineGroup of data.lines) {*/}
      {/*    for (const line of lineGroup.elements) {*/}
      {/*      for (const vocable of line) {*/}
      {/*        vocable.text = vocable.text.normalize("NFD").replace(/[\u064b-\u065b]/g, "")*/}
      {/*      }*/}
      {/*    }*/}
      {/*  }*/}

      {/*  setLines(loadLinesFromExternalCaptionTrack(data));*/}
      {/*}} >DO!</Button> : null}*/}

      {contentIssues ? <IssueManagerButton
        issues={contentIssues}
        setCurrentLineIdx={setCurrentLineIdx}
      /> : null}

      {props.playIntf ? <AudioControls
        playIntf={props.playIntf}
        currentLineIndex={currentLineIndex}
      /> : null}
    </div>

    <LineBrowser
      currentLineIndex={currentLineIndex}
      lineCount={lineCount}
      setCurrentLineIdx={setCurrentLineIdx}
      trackAPI={trackIntf}
      contentIssues={contentIssues}
    />

    <LinePreview
      words={words}
      translationLine={props.translation?.[currentLineIndex]}
    />

    <div style={{position: 'relative'}}>
      <LineTable
        trackAPI={props.trackIntf}
        trackVersionId={trackVersionId}
        contentIssues={contentIssues}
        words={words}
        lineIndex={currentLineIndex}
        nodeSuggestions={nodeSuggestions}
        isDataLoaded={hasData}
        forcedVocableMerges={combinedForcedVocableMerges}
        language={props.language}
        onCreateWordClick={handleCreateWordClick}
        onEditWordClick={handleEditWordClick}
        onOptionSelected={handleWordNodeSelected}
        onMergeVocables={handleMergeVocables}
        onSplitVocables={handleSplitVocables}
        onVocableTextChange={handleVocableTextChange}
        onAssignCustomNode={handleCustomNodeEdited}
        aiDrafts={aiDrafts}
        aiDecisionNotes={aiDecisionNotes}
        onApplyAIDraft={handleApplyAIDraft}
        onDismissAIDraft={(vocableIds) => removeAIDraftsForVocables(vocableIds, {exactOnly: true})}
      />

      {error ? <div className={styles.errorOverlay}>
        <h3>Error: Suggestions could not be loaded.</h3>
        <p>
          Try again by clicking first next, then previous.
        </p>
      </div> : null}
    </div>

  </div>
}


function AudioControls(props: {
  playIntf: PlayInterface,
  currentLineIndex: number
}) {
  const [autoPlay, setAutoPlay] = useLocalStorage<boolean>('languagetoolEditor.AudioControls.autoPlay', true);

  useEffect(() => {
    if (autoPlay) {
      props.playIntf.playLine(props.currentLineIndex);
    }
  }, [props.currentLineIndex]);

  return <div>
    <ButtonGroup color="primary" size={"small"}>
      <Button onClick={() => {
        props.playIntf.playLine(props.currentLineIndex);
      }}>Play!
      </Button>
      <ToggleButton
        value="autoplay"
        selected={autoPlay}
        onChange={() => {
          setAutoPlay(!autoPlay);
        }}
      >
        Auto Play
      </ToggleButton>
    </ButtonGroup>
  </div>
}